import { toPath } from 'lodash-es';

import { RunInputSchema, RunOutputSchema } from '@/types/schema';
import { maybeGetArrayOfMessages } from '@/utils/messages';
import { isSerializedConstructor } from '@/utils/serialized';

export type PlainPayloadMethod =
  | { type: 'key-heuristic' }
  | { type: 'key'; value: string }
  | { type: 'key'; value: string[] };

export function getWithNegativeArrayIndex(
  payload: RunInputSchema | RunOutputSchema | undefined,
  pathSegments: string[]
) {
  if (payload == null) return undefined;

  let current: unknown = payload;

  for (const segment of pathSegments) {
    if (current == null) {
      return undefined;
    }

    const segmentAsIdx = Number(segment);
    if (Array.isArray(current) && Number.isInteger(segmentAsIdx)) {
      const resolvedIndex =
        segmentAsIdx >= 0 ? segmentAsIdx : current.length + segmentAsIdx;

      if (resolvedIndex < 0 || resolvedIndex >= current.length) {
        return undefined;
      }
      current = current[resolvedIndex];
    } else if (typeof current === 'object') {
      current = (current as Record<string, unknown>)[segment];
    }
  }

  return current;
}

function getSingleValue(
  path: string,
  payload: RunInputSchema | RunOutputSchema | undefined,
  fullPathAsKey: boolean = false
) {
  const pathSegments = toPath(path);
  const value = getWithNegativeArrayIndex(payload, pathSegments);
  const lastKey = pathSegments[pathSegments.length - 1] ?? 'value';
  return { [fullPathAsKey ? path : lastKey]: value };
}

export function getPlainPayload(
  payload: RunInputSchema | RunOutputSchema | undefined,
  method: PlainPayloadMethod
) {
  if (!payload) return payload;
  if (isSerializedConstructor(payload)) return payload;
  if (typeof payload !== 'object') return payload;

  const objectPayload = payload as Record<string, unknown>;

  if (method.type === 'key') {
    if (Array.isArray(method.value)) {
      return method.value.reduce((acc, value) => {
        return { ...acc, ...getSingleValue(value, payload, true) };
      }, {});
    }
    return getSingleValue(method.value, payload);
  }

  // default heuristic
  // - find the list of messages in payload if present
  // - otherwise find the longest string key in payload
  const maybeMessagesArray = maybeGetArrayOfMessages(payload);
  if (maybeMessagesArray) {
    return maybeMessagesArray;
  }

  // find the list of messages in payload if present
  const messageKeys = Object.keys(objectPayload).filter(
    (k) => !!maybeGetArrayOfMessages(objectPayload[k])
  );
  if (messageKeys.length > 0) {
    return {
      [messageKeys[0]]: maybeGetArrayOfMessages(objectPayload[messageKeys[0]]),
    };
  }

  // find longest string key in payload
  const strKeys = Object.keys(objectPayload).filter(
    (k) => typeof objectPayload[k] === 'string'
  );

  strKeys.sort(
    (a, b) =>
      ((objectPayload[b] as string) ?? '').length -
      ((objectPayload[a] as string) ?? '').length
  );
  if (strKeys.length === 0) return payload;
  return { [strKeys[0]]: objectPayload[strKeys[0]] };
}
